// The endpaper — the colophon below the wall (plan `2026_09_01_toile_homepage_seo.md`). Quiet
// house register on the `--tl-*` tokens (`styles/index.scss`), same typography scale as the pack
// hub (`styles/Packs/packsHub.module.scss`'s `.eyebrow`/`.heading`/`.sub`) — this is an endpaper,
// not a landing page, so nothing here competes with the wall above it.

$maxw: 900px;
$gutter: 26px;

.endpaper {
    background: var(--tl-stage);
    // Generous top margin after the wall's last row (plan § 3: "so the structure reads as a
    // distinct colophon, no divider element") — a border would restate what the space already says.
    padding: 96px $gutter 0;
    display: flex;
    flex-direction: column;
    gap: 64px;
}

.intro,
.catalog,
.sections,
.faq {
    max-width: $maxw;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
}

.h1 {
    font-family: var(--tl-font);
    font-weight: 500;
    font-size: 30px;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: var(--tl-text);
    margin: 0 0 16px;

    @media (max-width: 640px) {
        font-size: 24px;
    }
}

.h2,
.sectionHeading {
    font-family: var(--tl-font);
    font-weight: 500;
    font-size: 20px;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: var(--tl-text);
    margin: 0 0 12px;
}

.lede {
    font-family: var(--tl-font);
    font-size: 15px;
    line-height: 1.6;
    color: var(--tl-muted);
    margin: 0 0 20px;
    max-width: 68ch;
}

.stats {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.06em;
    color: var(--tl-dim);
    margin: 0;
}

.catalogHeader {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
}

.headerLink {
    font-family: var(--tl-font);
    font-size: 13px;
    color: var(--tl-accent);
    white-space: nowrap;
}

.catalogList {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px 20px;
    list-style: none;
    margin: 0;
    padding: 0;

    @media (max-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
    }
}

.catalogLink {
    font-family: var(--tl-font);
    font-size: 13px;
    color: var(--tl-muted);
    line-height: 2;
}

.sections {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.section p {
    font-family: var(--tl-font);
    font-size: 14px;
    line-height: 1.65;
    color: var(--tl-muted);
    margin: 0;

    a {
        color: var(--tl-accent);
    }
}

.faqList {
    display: flex;
    flex-direction: column;
}

.faqItem {
    border-top: 1px solid var(--tl-border);
    padding: 14px 0;

    .iconOpen {
        display: none;
    }

    &[open] .iconClosed {
        display: none;
    }

    &[open] .iconOpen {
        display: inline;
    }

    &:last-child {
        border-bottom: 1px solid var(--tl-border);
    }
}

.faqItemTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    user-select: none;
    font-family: var(--tl-font);
    font-size: 14px;
    color: var(--tl-text);

    &::-webkit-details-marker,
    &::marker {
        display: none;
        content: '';
    }
}

.faqItemAnswer {
    font-family: var(--tl-font);
    font-size: 13px;
    line-height: 1.6;
    color: var(--tl-muted);
    margin: 10px 0 0;
}

// Featured-on strip — narcis's pre-footer pattern in the house register: mono eyebrow on `--tl-dim`,
// badges dimmed at rest, lifted on hover. Same 900px column as the sections above it.
.featuredOnSection {
    max-width: $maxw;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
    text-align: center;
}

.featuredOnLabel {
    display: block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tl-dim);
    margin-bottom: 18px;
}

.featuredOn {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 24px;
}

.featuredOnBadge {
    height: 40px;
    width: auto;
    opacity: 0.7;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 1;
    }
}
